import { RequestClient } from './request-client';
import type { Environments } from '@/features/environment/types';
import {
    CountJobAndTaskRequestQueryDto,
    CountResponseDto,
    JobByIdResponseDto,
    SearchRequestDto,
    SearchResponseDto,
    TaskByIdResponseDto,
    JobResultsResponseDto,
    TaskResultsResponseDto,
} from '@itential/orchestration-search-service-types';

export type WorkflowNameDto = {
    workflowId: string;
    workflowName: string;
};

/**
 * Central service for all OSS repository API requests
 * Provides a consistent interface for backend communication
 */
export class OSSRequestService {
    constructor(private _requestClient: RequestClient) {}

    /**
     * Fetches all available environments from the API
     * @returns Promise resolving to environments data
     */
    async getEnvironments(): Promise<Environments> {
        return await this._requestClient.get<Environments>('environments');
    }

    /**
     * Fetches a job by its ID
     * @param id - The job ID to fetch
     * @returns Promise resolving to job data
     */
    async getJobById(id: string): Promise<JobByIdResponseDto> {
        return await this._requestClient.get<JobByIdResponseDto>(`jobs/${id}`);
    }

    /**
     * Fetches a task by its ID
     * @param id - The task ID to fetch
     * @returns Promise resolving to task data
     */
    async getTaskById(id: string): Promise<TaskByIdResponseDto> {
        return this._requestClient.get<TaskByIdResponseDto>(`tasks/${id}`);
    }

    /**
     * Fetches jobs and/or tasks with search criterion
     * @param criterion - search criterion
     * @returns Promise resolving to job and/or task data
     */
    async search(criteria: SearchRequestDto): Promise<SearchResponseDto> {
        return await this._requestClient.post<SearchResponseDto>('search', criteria);
    }

    /**
     * Fetches the total count of jobs and/or tasks with count criterion
     * @param criterion - count criterion
     * @returns Promise resolving to a count (number) of jobs and/or tasks
     */
    async count(criteria: CountJobAndTaskRequestQueryDto): Promise<CountResponseDto> {
        return await this._requestClient.post<CountResponseDto>('count', criteria);
    }

    /**
     * Fetches workflows for a specific environment
     * @param environment - The environment to fetch workflows for
     * @returns Promise resolving to workflows data with IDs and names
     */
    async getWorkflowNames(environment: string): Promise<WorkflowNameDto[]> {
        const response = await this._requestClient.get<{ workflows: WorkflowNameDto[] }>(
            `workflows/names?environment=${environment}`,
        );
        return response.workflows;
    }

    /**
     * Fetches project names for a specific environment
     * @param environment - The environment to fetch project names for
     * @returns Promise resolving to project names data
     */
    async getProjectNames(environment: string): Promise<{ projectNames: string[] }> {
        return await this._requestClient.get<{ projectNames: string[] }>(
            `projects/names?environment=${environment}`,
        );
    }

    /**
     * Fetches presigned URLs for job results (input/output data)
     * @param jobId - The job ID to fetch results for
     * @returns Promise resolving to presigned URL data
     */
    async getJobResults(jobId: string): Promise<JobResultsResponseDto> {
        return await this._requestClient.get<JobResultsResponseDto>(`jobs/${jobId}/results`);
    }

    /**
     * Fetches presigned URLs for task results (input/output data)
     * @param taskId - The task ID to fetch results for
     * @returns Promise resolving to presigned URL data
     */
    async getTaskResults(taskId: string): Promise<TaskResultsResponseDto> {
        return await this._requestClient.get<TaskResultsResponseDto>(`tasks/${taskId}/results`);
    }
}
